import { n as useKeyboardShortcuts, t as DEFAULT_KEYBOARD_SHORTCUTS } from "./useKeyboardShortcuts-D47pg3fZ.js";
import { useDocSearchKeyboardEvents } from "./useDocSearchKeyboardEvents.js";
import { useTheme } from "./useTheme.js";
import React from "react";

//#region src/useIsMobile.ts
const useIsMobile = () => {
	const [isMobile, setIsMobile] = React.useState(false);
	React.useEffect(() => {
		const checkForMobile = () => {
			const isMobileMediaQuery = window.matchMedia("(max-width: 768px)");
			setIsMobile(isMobileMediaQuery.matches);
		};
		checkForMobile();
		window.addEventListener("resize", checkForMobile);
		return () => {
			window.removeEventListener("resize", checkForMobile);
		};
	}, []);
	return isMobile;
};

//#endregion
//#region \0@oxc-project+runtime@0.139.0/helpers/esm/objectWithoutPropertiesLoose.js
function _objectWithoutPropertiesLoose(r, e) {
	if (null == r) return {};
	var t = {};
	for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
		if (e.includes(n)) continue;
		t[n] = r[n];
	}
	return t;
}

//#endregion
//#region \0@oxc-project+runtime@0.139.0/helpers/esm/objectWithoutProperties.js
function _objectWithoutProperties(e, t) {
	if (null == e) return {};
	var o, r, i = _objectWithoutPropertiesLoose(e, t);
	if (Object.getOwnPropertySymbols) {
		var s = Object.getOwnPropertySymbols(e);
		for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
	}
	return i;
}

//#endregion
//#region src/DocSearch.tsx
const _excluded = [
	"children",
	"theme",
	"onReady",
	"onOpen",
	"onClose",
	"onSidepanelOpen",
	"onSidepanelClose"
];
const Context = React.createContext(void 0);
Context.displayName = "DocSearchContext";
function DocSearchInner(_ref, ref) {
	let { children, theme, onReady, onOpen, onClose, onSidepanelOpen, onSidepanelClose } = _ref, props = _objectWithoutProperties(_ref, _excluded);
	const [docsearchState, setDocsearchState] = React.useState("ready");
	const [initialQuery, setInitialQuery] = React.useState(props.initialQuery || "");
	const searchButtonRef = React.useRef(null);
	const keyboardShortcuts = useKeyboardShortcuts(props.keyboardShortcuts);
	const [initialAskAiMessage, setInitialAskAiMessage] = React.useState();
	const [registeredViews, setRegisteredViews] = React.useState(() => /* @__PURE__ */ new Set());
	const isMobile = useIsMobile();
	const prevStateRef = React.useRef("ready");
	const isModalActive = ["modal-search", "modal-askai"].includes(docsearchState);
	const isAskAiActive = docsearchState === "modal-askai";
	const isHybridModeSupported = !isMobile && registeredViews.has("sidepanel");
	const isSidepanelOpen = docsearchState === "sidepanel";
	React.useEffect(() => {
		onReady === null || onReady === void 0 || onReady();
	}, [onReady]);
	React.useEffect(() => {
		const prevState = prevStateRef.current;
		const currentState = docsearchState;
		if ((currentState === "modal-search" || currentState === "modal-askai") && prevState !== "modal-search" && prevState !== "modal-askai") onOpen === null || onOpen === void 0 || onOpen();
		if (currentState === "ready" && (prevState === "modal-search" || prevState === "modal-askai")) onClose === null || onClose === void 0 || onClose();
		if (currentState === "sidepanel" && prevState !== "sidepanel") onSidepanelOpen === null || onSidepanelOpen === void 0 || onSidepanelOpen();
		if (currentState !== "sidepanel" && prevState === "sidepanel") onSidepanelClose === null || onSidepanelClose === void 0 || onSidepanelClose();
		prevStateRef.current = currentState;
	}, [
		docsearchState,
		onOpen,
		onClose,
		onSidepanelOpen,
		onSidepanelClose
	]);
	const openModal = React.useCallback(() => {
		setDocsearchState("modal-search");
	}, []);
	const closeModal = React.useCallback(() => {
		var _searchButtonRef$curr, _props$initialQuery;
		setDocsearchState("ready");
		(_searchButtonRef$curr = searchButtonRef.current) === null || _searchButtonRef$curr === void 0 || _searchButtonRef$curr.focus();
		setInitialQuery((_props$initialQuery = props.initialQuery) !== null && _props$initialQuery !== void 0 ? _props$initialQuery : "");
	}, [setDocsearchState, props.initialQuery]);
	const onAskAiToggle = React.useCallback((active, initialMessage) => {
		if (!isMobile && active && isHybridModeSupported) {
			setInitialAskAiMessage(initialMessage);
			setDocsearchState("sidepanel");
			return;
		}
		setDocsearchState(active ? "modal-askai" : "modal-search");
	}, [
		setDocsearchState,
		isMobile,
		isHybridModeSupported
	]);
	const openSidepanel = React.useCallback((initialMessage) => {
		if (!registeredViews.has("sidepanel")) return;
		setInitialAskAiMessage(initialMessage);
		setDocsearchState("sidepanel");
	}, [setDocsearchState, registeredViews]);
	const onInput = React.useCallback((event) => {
		setDocsearchState("modal-search");
		setInitialQuery(event.key);
	}, [setDocsearchState, setInitialQuery]);
	const registerView = React.useCallback((view) => {
		if (registeredViews.has(view)) return;
		setRegisteredViews((prev) => {
			const newViews = new Set(prev);
			newViews.add(view);
			return newViews;
		});
	}, [registeredViews]);
	React.useImperativeHandle(ref, () => ({
		open: openModal,
		close: closeModal,
		openAskAi: (initialMessage) => onAskAiToggle(true, initialMessage),
		openSidepanel,
		get isReady() {
			return true;
		},
		get isOpen() {
			return isModalActive;
		},
		get isSidepanelOpen() {
			return isSidepanelOpen;
		},
		get isSidepanelSupported() {
			return isHybridModeSupported;
		}
	}), [
		openModal,
		closeModal,
		onAskAiToggle,
		openSidepanel,
		isModalActive,
		isSidepanelOpen,
		isHybridModeSupported
	]);
	useTheme({ theme });
	useDocSearchKeyboardEvents({
		isOpen: isModalActive,
		onOpen: openModal,
		onClose: closeModal,
		onAskAiToggle,
		onInput,
		isAskAiActive,
		searchButtonRef,
		keyboardShortcuts
	});
	const value = React.useMemo(() => ({
		docsearchState,
		setDocsearchState,
		searchButtonRef,
		initialQuery,
		keyboardShortcuts,
		openModal,
		closeModal,
		isAskAiActive,
		isModalActive,
		onAskAiToggle,
		initialAskAiMessage,
		registerView,
		isHybridModeSupported
	}), [
		docsearchState,
		searchButtonRef,
		initialQuery,
		keyboardShortcuts,
		openModal,
		closeModal,
		isAskAiActive,
		isModalActive,
		onAskAiToggle,
		initialAskAiMessage,
		registerView,
		isHybridModeSupported
	]);
	return /* @__PURE__ */ React.createElement(Context.Provider, { value }, children);
}
const DocSearch = React.forwardRef(DocSearchInner);
DocSearch.displayName = "DocSearch";
function useDocSearch() {
	const ctx = React.useContext(Context);
	if (ctx === void 0) throw new Error("`useDocSearch` must be used within the `DocSearch` provider");
	return ctx;
}

//#endregion
export { DEFAULT_KEYBOARD_SHORTCUTS, DocSearch, useDocSearch, useDocSearchKeyboardEvents, useKeyboardShortcuts, useTheme };
//# sourceMappingURL=index.js.map